import { startOfDay, format } from 'date-fns';
import { State } from "./../store/app";

import Chart from "./chart.js";

/* Charts globals */
Chart.defaults.global.defaultBorderColor = "white";
Chart.defaults.global.defaultFontColor = "white";
Chart.defaults.global.defaultFontFamily =
	'BlinkMacSystemFont, -apple-system, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", "Helvetica", "Arial", sans-serif';

Chart.defaults.global.animation.duration = 0;
Chart.defaults.global.hover.animationDuration = 0;

export default class AppCharts {
	constructor(
		public store: { state: State }
	) { }

	exists(value: number): boolean {
		return value !== undefined && value !== null && value !== 0;
	}

	getDataFor(field: string): { x: number; y: number }[] {
		const data: { [index: string]: any } = this.store.state.snapshots;
		const allKeys = Object.keys(data);
		const filteredKeys = allKeys
			.map(function (val, i, arr) {
				var time = parseInt(val);
				return {
					key: time,
					date: startOfDay(new Date(time * 1000)).getTime() / 1000,
				};
			})
			.filter(
				(val, i, arr) =>
					i === arr.findIndex((vv, ii, aa) => val.date === vv.date)
			)
			.map((val, i, arr) => val.key);
		return filteredKeys.map(function (val, i, arr) {
			return {
				x: val,
				y: parseInt(data[val][field])
			};
		});
	}

	getDifference(field: string, divideByHundred: boolean): number {
		const data: { [index: string]: any } = this.store.state.snapshots;
		const keys = Object.keys(data);
		if (keys.length < 2) {
			return 0;
		}
		const first = data[keys[0]][field];
		const last = data[keys[keys.length - 1]][field];
		return (last - first) / (divideByHundred ? 100.0 : first);
	}

	lineGreen(label: string, data: { x: number; y: number }[], yAxisID: string) {
		return this.line("#23d160", label, data, yAxisID);
	}

	lineRed(label: string, data: { x: number; y: number }[], yAxisID: string) {
		return this.line("#ff3860", label, data, yAxisID);
	}

	line(
		color: string,
		label: string,
		data: { x: number; y: number }[],
		yAxisID: string
	) {
		return {
			cubicInterpolationMode: "monotone",
			label,
			borderColor: color,
			backgroundColor: color,
			fill: false,
			data,
			yAxisID
		};
	}

	timeLabels(data: { x: number, y: number }[]): string[] {
		return data.map((p, i, a) => format(new Date(p.x * 1000), "DD MMM"));
	}

	get app() {
		return this.store.state.app;
	}

	get recent() {
		const positives = this.getDataFor("recentPositive");
		const negatives = this.getDataFor("recentNegative");
		return {
			labels: this.timeLabels(positives),
			datasets: [
				this.lineGreen("POSITIVE", positives, "left"),
				this.lineRed("NEGATIVE", negatives, "right")
			]
		};
	}

	get overall() {
		const positives = this.getDataFor("overallPositive");
		const negatives = this.getDataFor("overallNegative");
		return {
			labels: this.timeLabels(positives),
			datasets: [
				this.lineGreen("POSITIVE", positives, "left"),
				this.lineRed("NEGATIVE", negatives, "right")
			]
		};
	}

	get ratings() {
		const recent = this.getDataFor("recent");
		const overall = this.getDataFor("overall");
		let check = (arr: { x: number, y: number }[]) => !arr.every((val, i, a) => val.y == 0);
		let datasets: any[] = [];
		if (check(recent)) {
			datasets.push(this.line("#ffdd57", "RECENT", recent, "left"));
		}
		if (check(overall)) {
			datasets.push(this.line("#209cee", "OVERALL", overall, "right"));
		}
		return {
			labels: this.timeLabels(datasets.length > 0 ? datasets[0].data : []),
			datasets
		};
	}

	get purchases() {
		const steam = this.getDataFor("purchasesSteam");
		const other = this.getDataFor("purchasesOther");
		return {
			labels: this.timeLabels(steam),
			datasets: [
				this.line("#209cee", "STEAM", steam, "left"),
				this.line("#ffdd57", "OTHER", other, "right")
			]
		};
	}

	get discussions() {
		let posData: { x: number, y: number }[];
		let negData: { x: number, y: number }[];
		if (this.exists(this.app.ratingOverall)) {
			posData = this.getDataFor("overallPositive");
			negData = this.getDataFor("overallNegative");
		} else if (this.exists(this.app.ratingCurrent)) {
			posData = this.getDataFor("recentPositive");
			negData = this.getDataFor("recentNegative");
		} else {
			return [];
		}
		var lastPositive = posData.length == 0 ? 0 : posData[0].y;
		var lastNegative = negData.length == 0 ? 0 : negData[0].y;
		const keys = posData.map((val, i, a) => val.x);
		const positives = keys.map((key, i, a) => {
			var result = posData.find((point, i, a) => point.x == key);
			if (!!result) {
				var delta = result.y - lastPositive;
				lastPositive = result.y;
				return delta;
			}
			return null;
		});
		const negatives = keys.map((key, i, a) => {
			var result = negData.find((point, i, a) => point.x == key);
			if (!!result) {
				var delta = result.y - lastNegative;
				lastNegative = result.y;
				return delta;
			}
			return null;
		});

		if (keys.length > 0) {
			keys.splice(0, 1);
			positives.splice(0, 1);
			negatives.splice(0, 1);
		}
		return {
			labels: keys.map((val, i, a) => format(new Date(val * 1000), "DD MMM")),
			datasets: [
				{
					label: "POSITIVE",
					data: positives,
					borderColor: "#23d160",
					backgroundColor: "#23d160",
					borderWidth: 1
				},
				{
					label: "NEGATIVE",
					data: negatives,
					borderColor: "#ff3860",
					backgroundColor: "#ff3860",
					borderWidth: 1
				}
			]
		};
	}

	get statistics(): { title: string; value: number, invert: boolean }[] {
		const isAbsolute = (val: string) => (val == "recent") || (val == "overall");
		const items = {
			"recent": "Recent",
			"overall": "Overall",
			"recentPositive": "Positive (recent)",
			"recentNegative": "Negative (recent)",
			"overallPositive": "Positive (overall)",
			"overallNegative": "Negative (overall)"
		};

		const snapshots = this.store.state.snapshots;
		const times = Object.keys(snapshots);
		if (times.length === 0) {
			return [];
		}
		const app = snapshots[times[times.length - 1]];
		let result: { title: string; value: number, invert: boolean }[] = [];
		for (let key in items) {
			if (this.exists(app[key])) {
				let invert = key.indexOf("Neg") !== -1;
				result.push({
					title: items[key],
					value: this.getDifference(key, isAbsolute(key)),
					invert
				});
			}
		}
		return result;
	}

	get noChanges() {
		return (
			!!this.store.state.snapshots &&
			Object.keys(this.store.state.snapshots).length == 0
		);
	}
}
